import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import {useHotkeys} from 'react-hotkeys-hook';

import { editContent } from '../../modules/editor';
import { hasHttp, isIn, isUrl, isValidLink } from '../../util/validator';


import useAlert from '../../hooks/useAlert';

import Modal from '../Modal/Modal';
import { trimBlankString, urlToVideoId } from '../../util/string';
import MergeButton from '../MergeButton';
import { RootState } from '../../modules';
import { getPathAfterBuild } from '../../util/file';

type Props = {
	target: string;
	imagePath: string;
	name: string;
	value: any;
	imageType?: string;
	confirmText?: string;
	label?: string;
	valueType?: string;
	title?: string;
	isModalOpen?: boolean;
};

function ButtonModal({ target, title, value, imagePath, imageType, name, label, valueType, isModalOpen }: Props) {
	
	const dispatch = useDispatch();
	const accountPlan = useSelector((state: RootState) => state.auth.account.billing.plan);
	const isNoLinkValidationAccount = useSelector((state: RootState) => state.auth.account.options.editorNoLinkValidation)

	const dropdownRef = useRef<HTMLDivElement>(null);

	const Alert = useAlert();

	const inputElement= useRef<HTMLInputElement>(null);

	const [showModal, setShowModal] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [oldValue, setOldValue] = useState('');
	const [showDropdown, setShowDropdown] = useState(false);
	const [error, setError] = useState('');
	
	const iconImage = (path) => {
		const context = require.context('../../assets/images/', true);
		return getPathAfterBuild(context, path);
	};

	const { listVars, systemFields } = useSelector((state: RootState) => state.editor);

	const whitelistForUrl = useMemo(() => {
		const fields = systemFields?.systemMergeURLFields || [];
		if (valueType === 'url' || valueType === 'videoUrl') return fields?.map(({ substitution }) => `$%${substitution}%$`);
	}, [systemFields]);

	useEffect(() => {
		setInputValue(value[name]);
		setOldValue(value[name]);
	}, [value]);

	useEffect(() => {
		if(isModalOpen) {
			openModal();
		}
	}, [isModalOpen]);

	useEffect(() => {
		if (inputElement.current) {
			inputElement.current.focus();
		}
	}, [showModal])

	useHotkeys('Esc', () => {
		closeModal();
	}, { enableOnTags: ['INPUT'] });

	const openModal = () => {
		setShowModal(true);
		window.parent.postMessage({ msg: 'dialogShow' }, '*');
	}
	
	const closeModal = () => {
		setShowModal(false);
		setInputValue(oldValue);
		setError('');
		window.parent.postMessage({ msg: 'dialogHide' }, '*');
	}

	const onChangeLink = (e:any) => {
		const { value } = e.target;
		setInputValue((valueType === 'url' || valueType === 'videoUrl') ? trimBlankString(value) : value);
		setError('');
	}

	const onSelectMerge = (value: string) => {
		if (!(valueType === 'url' || valueType === 'videoUrl')) return;
		setInputValue(prev => trimBlankString(prev) + value);
		setError('');
	}

	const insertValue = (value:string) => {		
		setInputValue(value);
		setTimeout(() => {
			closeDropdown();
		}, 50);
	}

	const onKeyDown = (e:any) => {		
		if (e.key === 'Enter') {
			submit();
		}
		if (e.key === '$') {
			setShowDropdown(true);
		}		
	}
	useEffect(() => {
		const dropdown = dropdownRef.current;
		if (!showDropdown || dropdown == null) return;

		const hanlder = (event: MouseEvent) => {
			const { target } = event;
			const isNode = target instanceof Node;
			if (!isNode) return;
			const clickedInside = dropdown.contains(target);
			if (clickedInside) return;
			closeDropdown();
		};

		window.document.body.addEventListener('click', hanlder);
		return () => {
			window.document.body.removeEventListener('click', hanlder);
		}
	}, [showDropdown]);

	const closeDropdown = () => {
		const input = inputElement.current;
		if (input == null) return;
		setShowDropdown(false);
		input.focus();
		const { length } = input.value;
		input.setSelectionRange(length, length, 'backward');
	}

	const verifiedValue = (v: string): { isValid: boolean; value: any; videoId?: string } => {	
		let value = v;
		
		const invalidResult = { isValid: false, value: null };
		switch (valueType) {
			case 'url':
				if (value?.length === 0) {
					setError(Alert?.msg?.REQUIRE_URL);
					return invalidResult;
				}
				if (isNoLinkValidationAccount) {
					return { isValid: true, value }
				}
				if (!isValidLink(value, whitelistForUrl!)) {
					setError(Alert?.msg?.CHECK_URL_TYPE);
					return invalidResult;
				}
				if (isUrl(value) && !hasHttp(value)) {
					value = `http://${value}`;
				}
				break;
			case 'alt': 
				if (value?.length > 200) {
					setError(Alert?.msg?.ALT_TEXT_TOO_LONG);
					return invalidResult;
				}
				break;
			case 'videoUrl':
				if (value?.length === 0) {
					setError(Alert?.msg?.REQUIRE_URL);
					return invalidResult;
				}
				if (!isValidLink(value, whitelistForUrl!)) {
					setError(Alert?.msg?.CHECK_URL_TYPE);
					return invalidResult;
				}
				if (isUrl(value) && !hasHttp(value)) {
					value = `http://${value}`;
				}
				const videoId = urlToVideoId(value);
				if (!videoId) {
					setError(Alert?.msg?.VIDEO_ID_ERROR);
					return invalidResult;
				}
				return { isValid: true, value, videoId };
				break;
			default:
				break;
		}
		return { isValid: true, value };
	}

	const submit = () => {
		const { isValid, value, videoId } = verifiedValue(inputValue);

		if (!isValid) {
			Alert.error(Alert?.msg?.DEFAULT_ERROR);
			return;
		}

		setOldValue(inputValue);
		if (valueType === 'videoUrl') {
			dispatch(editContent({ target: `${target}/${name}`, data: value, videoId }));
		} else {
			dispatch(editContent({ target: `${target}/${name}`, data: value }));
		}
		closeModal();	
	}

	const onMergeClose = () => inputElement.current?.focus();

	return (
		<>
			<button
				className={ inputValue?.length !== 0 ? 'btn active' : 'btn'}
				onClick={openModal}>
				{
					imageType === 'svg' ? (
						<ReactSVG src={iconImage(`./${imagePath}.${imageType}`)} height="16" />
					) : (
						<img src={iconImage(`./${imagePath}.${imageType}`)} height="16" />
					)
				}
			</button>
			<Modal
				show={showModal}
				close={closeModal}
			>
				<div className="link-form">
					{
						title !== '' && (
							<div className="link-form-header">
								<h1>{title}</h1>
							</div>
						)
					}
					<div className="link-form-body">
						<fieldset>
							<label >
								{label ? label : ''}
								<div className="link-form-body-input">
									<input 
										ref={inputElement}
										value={inputValue} 
										type={(valueType === 'url' || valueType === 'videoUrl') ? 'url' : 'text'} 
										onChange={onChangeLink} 
										onKeyDown={onKeyDown} 
									/>
									{(valueType === 'url' || valueType === 'videoUrl') && <MergeButton mode="inputright" accountPlan={accountPlan} systemFields={systemFields?.systemMergeURLFields || []} listVars={listVars} onSelect={onSelectMerge} onClose={onMergeClose} />}
								</div>
							</label>
							{
								showDropdown && (
								<div ref={dropdownRef} className="link-form-dropdown">
									<button onClick={() => {console.log('click');insertValue('$%unsubscribe%$')}}><strong>수신거부</strong><em>$%unsubscribe%$</em></button>
									<button onClick={() => {insertValue('$%permalink%$')}}><strong>웹에서 보기</strong><em>$%permalink%$</em></button>
								</div>
								)
							}
							{
								error?.length !== 0 && (
									<div className="msg error">
										{error}
									</div>
								)
							}
						</fieldset>
					</div>
					<div className="btns">
						<button className="btn medium tertiary" onClick={closeModal}>취소</button>
						<button className="btn medium secondary" onClick={submit}>저장하기</button>
					</div>
				</div>
			</Modal>
		</>
	);
}

ButtonModal.defaultProps = {
	imageType: 'png',
	label: '',
	valueType: 'url',
	title: '',
	isModalOpen: false
};

export default ButtonModal;